import {Injectable} from '@angular/core';
import {ChartConstants} from '@constants/pages/chart-constants';
import {StorageService} from '@services/storage/storage.service';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {TranslationService} from '@services/translation/translation.service';
import {DATA_TYPE} from '@constants/data-type.constant';

@Injectable({
  providedIn: 'root'
})
export class ChartSeriesSettingsGeneratorService {

  constructor(private chartsHelperService: ChartsHelperService,
              private storageService: StorageService,
              private translationService: TranslationService) {

  }

  public createSeries(isLineChart: boolean, machineId: string, usedSignals: any[], axisArray: any[], data: any[], machineSignals: any[]) {
    let signals = usedSignals;
    if (isLineChart) {
      signals = this.getLineChartAllSignalsAndAxis(axisArray, isLineChart);
    }

    const length = signals.length;

    const series: any[] = [];
    for (let i = 0; i < length; i++) {

      const signal = signals[i];
      let machineSignal: any = undefined;
      let displayedText: string = '';
      if (isLineChart) {
        machineSignal = this.chartsHelperService.getLineWidgetMachineSignalById(signal.signalId, machineSignals);
        displayedText = this.translationService.translate.instant(machineSignal?.label || signal.displayedText);
      } else {
        machineSignal = this.chartsHelperService.getMachineSignalById(signal.signalId, machineSignals);
        displayedText = this.translationService.translate.instant(machineSignal?.displayedText || signal.displayedText || machineSignal?.label);
      }

      let color;
      if (this.isDefined(signal.colors.main)) {
        color = signal.colors.main;
      } else {
        color = String(ChartConstants.DEFAULT_CHART_SETTINGS.SIGNAL.COLORS.MAIN);
      }

      const serieSettings: any = {
        type: 'line',
        color: this.chartsHelperService.createHex(color, isLineChart),
        name: displayedText,
        yAxis: signal.axisIndex ? Number(signal.axisIndex) : (signal.customRange ? (i + 1) : 0),
        marker: {
          symbol: 'circle',
          enabled: false
        },
        data: this.getData(signal.signalName, signal.signalId, data, isLineChart)
      };

      if (!signal.id || signal.id.length === 0) {
        serieSettings.id = signal.signalId + '';
        serieSettings.linkedTo = signal.axisIndex;
        serieSettings.name = displayedText;
        serieSettings.signalName = signal.signalName;
      } else {
        serieSettings.id = signal.id + '';
        serieSettings.name = displayedText;
        serieSettings.signalName = signal.displayedText;
      }

      if (isLineChart) {
        serieSettings.showInLegend = signal.signalType !== DATA_TYPE.STRING;
        if (signal.signalType === DATA_TYPE.BOOL || signal.signalType === DATA_TYPE.BOOLEAN) {
          serieSettings.type = 'area';
          serieSettings.fillOpacity = 0.15;
        }
      } else {
        serieSettings.visible = this.getSignalOrRangeVisibility(machineId, signal.signalId, true, true);
      }

      series.push(serieSettings);

      if (!isLineChart) {
        const serieRangeSettings = {
          id: signal.signalId,
          color: this.chartsHelperService.createHex(color, isLineChart),
          signalName: signal.signalName + '_range',
          name: displayedText + '_range',
          yAxis: signal.customRange ? (i + 1) : 0,
          type: 'arearange',
          lineWidth: 0,
          linkedTo: ':previous',
          fillOpacity: 0.3,
          zIndex: 0,
          visible: this.getSignalOrRangeVisibility(machineId, signal.signalId, false, false),
          marker: {
            symbol: 'circle',
            enabled: false
          },
          data: this.getSeriesData(signal.signalName + '_range', data)
        };
        series.push(serieRangeSettings);
      }
    }
    return series;
  }

  private getData(signalName: string, signalId: string, data: any[], isLineChart: boolean) {
    if (isLineChart) {
      return this.getSeriesLineData(signalId, data);
    } else {
      return this.getSeriesData(signalName, data);
    }
  }

  private getSeriesData(signalName: string, data: any[]) {
    if (!signalName || !data.hasOwnProperty(signalName)) {
      return [];
    }
    return data[signalName];
  }

  private getSeriesLineData(signalId: string, data: any[]) {
    if (!signalId) {
      return [];
    }
    const result: any[] = [];
    for (const chartDataResponse of data) {
      for (const value of chartDataResponse.vs) {
        if (value.s === String(signalId)) {
          const numAr: number[] = [chartDataResponse.ts, Number(value.v)];
          result.push(numAr);
        }
      }
    }
    return result;
  }

  private isDefined(value) {
    return typeof value !== 'undefined';
  }


  private getSignalOrRangeVisibility(machineId: string, signalName: string, isSignal: boolean, fallbackValue: boolean) {

    const storageKey = 'interactiveDataAnalysis_signalsVisibility_' + machineId;
    const valueFromSessionStorage = this.storageService.readValueFromSessionStorage(storageKey);
    if (!valueFromSessionStorage) {
      return fallbackValue;
    }

    const storageParsedItems = JSON.parse(valueFromSessionStorage);
    if (storageParsedItems === null || !this.isDefined(storageParsedItems)) {
      return fallbackValue;
    }

    if (storageParsedItems[signalName] === null || !this.isDefined(storageParsedItems[signalName])) {
      return fallbackValue;
    }
    if (isSignal) {
      return this.isDefined(storageParsedItems[signalName].isSignalVisible) ? storageParsedItems[signalName].isSignalVisible : true;
    }
    return this.isDefined(storageParsedItems[signalName].isRangeVisible) ? storageParsedItems[signalName].isRangeVisible : false;

  }

  private getLineChartAllSignalsAndAxis(axisList, isLineChart: boolean) {

    if (!axisList || axisList.length === 0) {
      return [];
    }
    const signals: any[] = [];
    axisList = this.sortAxisListByLabel(axisList);
    for (let a = 0; a < axisList.length; a++) {
      const axis = axisList[a];
      signals.push(this.createSeriesForAxis(axis, a, isLineChart));

      let axisSignals = axis.signals;
      if (axisSignals && axisSignals.length > 0) {
        axisSignals = this.sortSignalsByLabel(axisSignals);
        for (const signal of axisSignals) {
          signal.axisIndex = isLineChart ? String(a) : String(a + 1);
          signal.axisYLabel = axis.axisYLabel;
          signals.push(signal);
        }
      }
    }
    return signals;
  }

  private sortAxisListByLabel(axisList) {
    return axisList.sort(function (a1, a2) {
      const label1 = a1.axisYLabel ? a1.axisYLabel.toLowerCase() : '';
      const label2 = a2.axisYLabel ? a2.axisYLabel.toLowerCase() : '';
      return (label1 < label2) ? -1 : (label1 > label2) ? 1 : 0;
    });
  }

  private createSeriesForAxis(axis, index, isLineChart: boolean) {
    return {
      id: index + '',
      axisId: axis.id,
      displayedText: axis.axisYLabel,
      colors: {main: this.chartsHelperService.determineLineChartAxisColor(axis, isLineChart)}

    };

  }

  private sortSignalsByLabel(signals) {
    return signals.sort(function (s1, s2) {
      const label1 = s1.displayedText ? s1.displayedText.toLowerCase() : '';
      const label2 = s2.displayedText ? s2.displayedText.toLowerCase() : '';
      if (label1 > label2) {
        return (label1 < label2) ? -1 : 1;
      } else {
        return (label1 < label2) ? -1 : 0;
      }
    });
  }
}
